import { useState, useContext, useEffect, useMemo } from "react";
import FormDetails from "../FormDetails/FormDetails";
import FormSection from "../FormSection/FormSection";
import TextInput from "../Inputs/TextInput";
import { v4 as uuidv4 } from "uuid";
import "./FormPreview.less";
import AddAction from "../AddAction/AddAction";
import AddActionWrapper from "../AddAction/AddActionWrapper";
import { DECORATORS, FormBuilderContext } from "../FormBuilderContext";
import FormInfo from "../FormInfo/FormInfo";
import FormMethods from "../FormMethods/FormMethods";

import FormColumns from "../FormColumns/FormColumns";
import ElementSettings from "../ElementsSettings/ElementSettings";
import ElementSettingsSimple from "../ElementSettingsSimple/ElementSettingsSimple";
import { getModelDetails } from "../../../../services/restAPI/FormsV3";
import { fieldMapper } from "./fieldMapper";
import { useIntl } from "react-intl";

const fetchModelDetails = async (selectedForm) => {
   const key =
      typeof selectedForm === "object" && selectedForm !== null ? selectedForm.value : selectedForm;
   if (typeof key !== "string") return [];
   const [module, model] = key.split("-");
   if (!module || !model || module === "primitives") return [];
   const res = await getModelDetails(module, model);

   if (res?.data) {
      return res.data;
   } else {
      return [];
   }
};

const FormPreview = () => {
   const [fields, setFields] = useState([]);
   const [editedElement, setEditedElement] = useState(null);
   const [editingTabId, setEditingTabId] = useState(null);
   const [editingLabel, setEditingLabel] = useState("");

   const {
      previewMode,
      listItems,
      setListItems,
      selectedForm,
      tabs,
      tabsItems,
      activeTabId,
      setActiveTabId,
      hasTabs,
      hasMultipleTabs,
      addTab,
      renameTab,
      removeTab,
   } = useContext(FormBuilderContext);

   const allUsedItems = useMemo(
      () => (hasTabs ? Object.values(tabsItems).flat() : listItems),
      [hasTabs, tabsItems, listItems],
   );

   // In preview mode show tab bar only when tabs exist; in edit mode always show it (for the + button)
   const showTabsBar = hasTabs || !previewMode;

   const confirmTabRename = () => {
      if (editingTabId !== null) {
         renameTab(editingTabId, editingLabel);
         setEditingTabId(null);
      }
   };

   const handleAddTab = () => {
      const newTab = addTab();
      setEditingTabId(newTab.id);
      setEditingLabel(newTab.label);
   };

   const intl = useIntl();

   useEffect(() => {
      (async () => {
         if (!selectedForm) return null;
         const data = await fetchModelDetails(selectedForm);

         setFields(data);
         setEditedElement(null);
      })();
   }, [selectedForm]);

   if (!selectedForm) return null;

   const removeInput = (index) => {
      const list = [...listItems];
      list.splice(index, 1);
      setListItems(list);
   };

   const addElementInIndex = (element, index) => {
      if (index === -1) {
         setListItems((prev) => {
            return [...prev, element];
         });
      } else {
         setListItems((prev) => {
            const next = [...prev];
            next.splice(index, 0, element);
            return next;
         });
      }
   };

   const ensurePositionDecorator = (decorators = [], position = "") => {
      const idx = decorators.findIndex((d) => d?.name === "PositionDecorator");
      if (idx === -1)
         return [...decorators, { name: "PositionDecorator", properties: { position } }];
      const next = [...decorators];
      next[idx] = { ...next[idx], properties: { ...(next[idx].properties || {}), position } };
      return next;
   };

   const addInput = (item, index, subIndex = null) => {
      if (!item) {
         console.warn("addInput called with undefined item at index", index, "subIndex", subIndex);
         return;
      }

      const input = {
         uuid: uuidv4(),
         ...item,
         label: item.name,
         placeholder: "",
         constraints: [{ name: item.name }],
         multiplicity: item.multiplicity,
         type: item.type,
         Input: fieldMapper[item.type] || TextInput,
         decorators: Object.values(DECORATORS),
      };
      if (subIndex !== null) {
         const position = subIndex === 0 ? "left" : "right";

         setListItems((prev) => {
            const next = [...prev];
            let target = next[index];

            if (!target || target.type !== "columns") {
               const columnEl = {
                  uuid: uuidv4(),
                  type: "columns",
                  items: [null, null],
                  displaySort: target?.displaySort ?? index + 1,
               };
               next.splice(index, 0, columnEl);
               target = columnEl;
            }

            const column = {
               ...target,
               items: Array.isArray(target.items) ? target.items.slice() : [null, null],
               displaySort: target.displaySort ?? index + 1,
            };

            const inputWithDecorators = {
               ...input,
               parentIndex: index,
               displaySort: column.displaySort,
               decorators: ensurePositionDecorator(input.decorators ?? [], position),
            };

            column.items[subIndex] = inputWithDecorators;
            next[index] = column;
            return next;
         });

         return;
      }

      addElementInIndex(input, index);
   };

   const handleAction = (action, element, index, subIndex) => {
      const isAdd = action === "add";

      switch (element) {
         case "fields":
            if (isAdd) {
               let idx = index;
               fields.forEach((el) => {
                  addInput(el, idx);
                  idx++;
               });
            }
            break;
         case "section":
            if (isAdd) {
               const sectionEl = {
                  uuid: uuidv4(),
                  type: "section",
                  items: [],
                  displaySort: index + 1,
               };
               addElementInIndex(sectionEl, index);
            }
            break;
         case "columns":
            if (isAdd) {
               const columnEl = {
                  uuid: uuidv4(),
                  type: "columns",
                  items: [null, null],
                  displaySort: index + 1,
               };
               addElementInIndex(columnEl, index);
            }
            break;
         case "listTable":
            if (isAdd) {
               const listTable = createListTableItem({
                  label: intl.formatMessage({
                     id: "form.add-list-table-label",
                     defaultMessage: "Lista",
                  }),
                  listId: "",
               });
               addElementInIndex(listTable, index);
            }
            break;
         default: {
            const el = fields.find(({ name }) => name === element);
            addInput(el, index, subIndex);
            break;
         }
      }
   };

   const handleStartEdit = (index, subIndex) => {
      let item = listItems[index];
      if (subIndex !== undefined) {
         item = item.items?.[subIndex];
         if (item) item.parentIndex = index;
      }
      setEditedElement(item);
   };

   const handleElementAction = (type, options) => {
      const element = options?.element || editedElement;

      const index = listItems.findIndex((item) => {
         return item.uuid === element.uuid;
      });
      const items = [...listItems];

      switch (type) {
         case "duplicate":
            const item = { ...items[index] };
            item.uuid = uuidv4();
            const newIndex = index + 1;
            if (item.items) {
               item.items = item.items.map((subItem) =>
                  subItem ? { ...subItem, uuid: uuidv4(), parentIndex: newIndex } : null,
               );
            }
            items.splice(newIndex, 0, item);
            setListItems(items);
            break;
         case "edit":
            if (editedElement.parentIndex >= 0) {
               const parent = items[editedElement.parentIndex];
               const childIdx = parent.items.findIndex((item) => item?.uuid === editedElement.uuid);
               for (const key in options) {
                  const decoratorsField = parent.items[childIdx]?.decorators ?? [];
                  const matchDecoratorIndex = decoratorsField.findIndex(
                     (decorator) => decorator.properties && key in decorator.properties,
                  );
                  if (matchDecoratorIndex !== -1) {
                     parent.items[childIdx].decorators[matchDecoratorIndex] = {
                        ...parent.items[childIdx].decorators[matchDecoratorIndex],
                        properties: {
                           ...(parent.items[childIdx].decorators[matchDecoratorIndex].properties ||
                              {}),
                           [key]: options[key],
                        },
                     };
                  } else {
                     parent.items[childIdx][key] = options[key];
                  }
               }
            } else {
               for (const key in options) {
                  const decoratorsField = items[index].decorators;
                  const matchDecoratorIndex =
                     decoratorsField?.findIndex(
                        (decorator) => decorator.properties && key in decorator.properties,
                     ) ?? -1;
                  if (matchDecoratorIndex !== -1) {
                     items[index].decorators[matchDecoratorIndex] = {
                        ...items[index].decorators[matchDecoratorIndex],
                        properties: { [key]: options[key] },
                     };
                  } else {
                     items[index][key] = options[key];
                  }
               }
            }
            setListItems(items);
            break;
         case "delete": {
            const hasKnownParent =
               options?.parentIndex !== undefined || (element?.parentIndex ?? -1) >= 0;

            if (hasKnownParent) {
               const columnIndex = options?.parentIndex ?? element.parentIndex;
               const cellIndex =
                  options?.subIndex !== undefined
                     ? options.subIndex
                     : items[columnIndex]?.items?.findIndex?.(
                          (cell) => cell?.uuid === element?.uuid,
                       );

               const columnRow = items[columnIndex];
               if (columnRow?.type === "columns") {
                  const cells = Array.isArray(columnRow.items)
                     ? columnRow.items.slice()
                     : [null, null];
                  if (cellIndex >= 0 && cellIndex < cells.length) {
                     cells[cellIndex] = null;
                     items[columnIndex] = { ...columnRow, items: cells };
                     setListItems(items);
                     break;
                  }
               }
            }

            const selfIndex = items.findIndex((it) => it.uuid === element.uuid);
            if (selfIndex >= 0) items.splice(selfIndex, 1);
            setListItems(items);
            break;
         }
         case "move": {
            const { direction } = options;
            if (direction === "up") {
               if (index - 1 < 0) return;
               const [removed] = items.splice(index, 1);
               items.splice(index - 1, 0, removed);
            }

            if (direction === "down") {
               if (index + 1 >= items.length) return;

               const [removed] = items.splice(index, 1);
               items.splice(index + 1, 0, removed);
            }
            setListItems(items);
            break;
         }
         default:
            break;
      }
   };

   const createListTableItem = ({
      label = "Lista",
      listId,
      listingSize = 20,
      defaultFilter = {},
   }) => ({
      uuid: uuidv4(),
      type: "listTable",
      label,
      placeholder: label,
      name: "readOnly_ListTable",
      multiplicity: "SINGULAR",
      Input: fieldMapper["primitives.ListTable"],
      decorators: Object.values(DECORATORS),
      displaySort: 0,

      modelField: {
         name: "readOnly_ListTable",
         label,
         type: {
            module: "primitives",
            name: "ListTable",
            isPrimitive: true,
         },
         constraints: [],
         multiplicity: "SINGULAR",
         list: {
            listId,
            listingSize,
            defaultFilter,
         },
      },
   });

   return (
      <div>
         <div className="form-builder">
            <div
               className={`form-builder-preview ${
                  previewMode ? "form-builder-preview--preview" : ""
               }`}
            >
               {showTabsBar && (
                  <div className="form-builder-tabs">
                     {hasMultipleTabs &&
                        tabs.map((tab) => {
                           const isActive = activeTabId === tab.id;
                           const isEditing = !previewMode && editingTabId === tab.id;
                           return (
                              <div
                                 key={tab.id}
                                 className={`form-builder-tabs__tab${isActive ? " form-builder-tabs__tab--active" : ""}`}
                                 onClick={() => {
                                    if (!isActive) {
                                       confirmTabRename();
                                       setActiveTabId(tab.id);
                                       setEditedElement(null);
                                    }
                                 }}
                              >
                                 {isEditing ? (
                                    <input
                                       autoFocus
                                       className="form-builder-tabs__tab-input"
                                       value={editingLabel}
                                       onChange={(e) => setEditingLabel(e.target.value)}
                                       onBlur={confirmTabRename}
                                       onKeyDown={(e) => {
                                          if (e.key === "Enter") confirmTabRename();
                                          if (e.key === "Escape") setEditingTabId(null);
                                       }}
                                       onClick={(e) => e.stopPropagation()}
                                    />
                                 ) : (
                                    <>
                                       {tab.label}
                                       {!previewMode && (
                                          <span
                                             className="form-builder-tabs__tab-edit"
                                             onClick={(e) => {
                                                e.stopPropagation();
                                                setEditingTabId(tab.id);
                                                setEditingLabel(tab.label);
                                             }}
                                          >
                                             ✎
                                          </span>
                                       )}
                                       {!previewMode && hasMultipleTabs && (
                                          <span
                                             className="form-builder-tabs__tab-remove"
                                             onClick={(e) => {
                                                e.stopPropagation();
                                                removeTab(tab.id);
                                             }}
                                          >
                                             ×
                                          </span>
                                       )}
                                    </>
                                 )}
                              </div>
                           );
                        })}
                     {!previewMode && (
                        <button className="form-builder-tabs__add" onClick={handleAddTab}>
                           +
                        </button>
                     )}
                  </div>
               )}
               <div className="fields">
                  <FormDetails fields={listItems} />
                  <div
                     className={
                        "form-builder-body " + (previewMode ? "form-builder-body-preview" : "")
                     }
                  >
                     {listItems.map((item, index) => {
                        const key = item.uuid;
                        if (item.type.toLowerCase() === "section") {
                           return (
                              <div key={key}>
                                 {!previewMode && editedElement?.uuid === item.uuid && (
                                    <div className="form-builder-edited-element-indicator"></div>
                                 )}
                                 <FormSection
                                    fields={fields}
                                    item={item}
                                    addInput={(...props) => {
                                       addInput(...props, index);
                                    }}
                                    onRemove={() => removeInput(index)}
                                    onAction={(...props) => {
                                       handleAction(...props, index);
                                    }}
                                    onStartEdit={() => {
                                       handleStartEdit(index);
                                    }}
                                    onDelete={() => {
                                       handleElementAction("delete", { element: item });
                                       if (editedElement?.uuid === item.uuid) {
                                          setEditedElement(null);
                                       }
                                    }}
                                    onDuplicate={() => {
                                       handleElementAction("duplicate", { element: item });
                                    }}
                                 />
                                 <AddActionWrapper key={`${key}-action`}>
                                    <AddAction
                                       onAction={(type, action) => {
                                          handleAction(type, action, index + 1);
                                       }}
                                       canAddAction={true}
                                       fields={fields}
                                       fieldsUsed={allUsedItems}
                                    />
                                 </AddActionWrapper>
                              </div>
                           );
                        }

                        if (item.type === "columns") {
                           return (
                              <div key={key}>
                                 {!previewMode && editedElement?.uuid === item.uuid && (
                                    <div className="form-builder-edited-element-indicator"></div>
                                 )}
                                 <FormColumns
                                    items={item.items}
                                    fields={fields}
                                    addInput={(item, subIndex) => {
                                       addInput(item, index, subIndex);
                                    }}
                                    onAction={(...props) => {
                                       handleAction(...props, index);
                                    }}
                                    onStartEdit={() => {
                                       handleStartEdit(index);
                                    }}
                                    onDelete={() => {
                                       handleElementAction("delete", { element: item });
                                       if (editedElement?.uuid === item.uuid) {
                                          setEditedElement(null);
                                       }
                                    }}
                                    onDuplicate={() => {
                                       handleElementAction("duplicate", { element: item });
                                    }}
                                    onSubStartEdit={(subIndex) => {
                                       handleStartEdit(index, subIndex);
                                    }}
                                    onSubDelete={(subIndex) => {
                                       const element = listItems[index]?.items?.[subIndex];
                                       if (element) {
                                          handleElementAction("delete", {
                                             element,
                                             parentIndex: index,
                                             subIndex,
                                          });
                                       }
                                    }}
                                    AddActionBtn={(subIndex) => (
                                       <AddAction
                                          onAction={(type, action) => {
                                             handleAction(type, action, index, subIndex);
                                          }}
                                          canAddAction={true}
                                          fields={fields}
                                          onlyFields
                                          fieldsUsed={allUsedItems}
                                       />
                                    )}
                                 />
                                 <AddActionWrapper key={`${key}-action`}>
                                    <AddAction
                                       onAction={(type, action) => {
                                          handleAction(type, action, index + 1);
                                       }}
                                       canAddAction={true}
                                       fields={fields}
                                       fieldsUsed={allUsedItems}
                                    />
                                 </AddActionWrapper>
                              </div>
                           );
                        }

                        const Input = item.Input;

                        return Input ? (
                           <div key={key} data-key={key}>
                              {!previewMode && editedElement?.uuid === item.uuid && (
                                 <div className="form-builder-edited-element-indicator"></div>
                              )}
                              <Input
                                 item={item}
                                 onStartEdit={() => {
                                    handleStartEdit(index);
                                 }}
                                 onDelete={() => {
                                    handleElementAction("delete", { element: item });
                                    if (editedElement?.uuid === item.uuid) {
                                       setEditedElement(null);
                                    }
                                 }}
                                 onDuplicate={() => {
                                    handleElementAction("duplicate", { element: item });
                                 }}
                              />
                              <AddActionWrapper key={`${key}-action`}>
                                 <AddAction
                                    onAction={(...props) => handleAction(...props, index + 1)}
                                    canAddAction={true}
                                    fields={fields}
                                    fieldsUsed={allUsedItems}
                                 />
                              </AddActionWrapper>
                           </div>
                        ) : null;
                     })}
                  </div>

                  <div className="form-builder-actions">
                     {!previewMode ? (
                        <AddAction
                           onAction={(...props) => handleAction(...props, -1)}
                           canAddAction={true}
                           fields={fields}
                           fieldsUsed={allUsedItems}
                        />
                     ) : null}
                  </div>
               </div>
            </div>

            {editedElement && !previewMode && (
               <div key={editedElement.uuid} className="form-builder-element-settings-wrapper">
                  {["section", "columns"].includes(editedElement.type) ? (
                     <ElementSettingsSimple
                        item={editedElement}
                        onAction={handleElementAction}
                        onClose={() => setEditedElement(null)}
                     />
                  ) : (
                     <ElementSettings
                        item={editedElement}
                        onAction={handleElementAction}
                        onClose={() => setEditedElement(null)}
                     />
                  )}
               </div>
            )}
         </div>
         <FormMethods />
         <FormInfo selectedForm={selectedForm} />
      </div>
   );
};

export default FormPreview;
